/**
 * AuiCalendar — the registered `Calendar` AUI renderer.
 *
 * Renders Day / 5-day / Week views in compact (list) or full (timeline)
 * density. Data is read inline from `events` or, preferably, loaded from a
 * JSON file via `eventsUrl` (VFS or plain URL) using `useVfsContent` — same
 * pattern as `AuiTable.rowsUrl`, so it live-refreshes on `vfs.file.updated`.
 *
 * Phase 1 is view-only with client-side navigation; the chosen view/density/
 * day persist via `useSurfaceNodeUiState` so agent surface updates don't reset
 * the user's place. Navigation is bounded to days that actually have data.
 */

import { useEffect, useMemo, useState } from 'react';
import { useVfsContent } from '@/hooks/useVfsContent';
import { Skeleton } from '@/components/ui/skeleton';
import type { AuiRendererProps } from '../../registry';
import { CalendarHeader } from './CalendarHeader';
import { DayView } from './DayView';
import { WeekView } from './WeekView';
import {
  parseCalendarData,
  getDataDays,
  getViewDays,
  getTimeBounds,
  prevDataDay,
  nextDataDay,
  stepWeek,
  viewHasData,
  fromISODate,
  toISODate,
  startOfDay,
  eventsForDay,
  formatDayLong,
  formatRangeLabel,
  type DataDays,
} from './calendar.helpers';
import type {
  CalendarEvent,
  CalendarView,
  CalendarDensity,
  CalendarUiState,
  CalendarDisplay,
  TimezoneSpec,
  NormalizedEvent,
} from './calendar.types';
import { useSurfaceNodeUiState } from '../../AuiSurfaceContext';

const VALID_VIEWS: CalendarView[] = ['day', 'fiveDay', 'week'];
const VALID_DENSITY: CalendarDensity[] = ['compact', 'full'];

/** Parse the `timezones` prop (array of IANA strings or {tz,label}). Cap 2. */
function parseTimezones(raw: unknown): TimezoneSpec[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const specs = raw
    .map((z): TimezoneSpec | null => {
      if (typeof z === 'string') return { tz: z };
      if (z && typeof z === 'object' && typeof (z as { tz?: unknown }).tz === 'string') {
        return { tz: (z as { tz: string }).tz, label: (z as { label?: string }).label };
      }
      return null;
    })
    .filter((z): z is TimezoneSpec => z !== null)
    .slice(0, 2);
  return specs.length > 0 ? specs : undefined;
}

function snapToDataDay(d: Date, data: DataDays): Date {
  if (data.days.length === 0) return d;
  const t = d.getTime();
  let best = data.days[0];
  let bestDiff = Math.abs(best.getTime() - t);
  for (const day of data.days) {
    const diff = Math.abs(day.getTime() - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = day;
    }
  }
  return best;
}

export function AuiCalendar({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const eventsUrl = props.eventsUrl as string | undefined;
  const inlineEvents = props.events as CalendarEvent[] | undefined;
  const initialView = (VALID_VIEWS.includes(props.view as CalendarView)
    ? (props.view as CalendarView)
    : 'week') as CalendarView;
  const initialDensity = (VALID_DENSITY.includes(props.density as CalendarDensity)
    ? (props.density as CalendarDensity)
    : 'full') as CalendarDensity;
  const propDate = props.date as string | undefined;
  const dayStart = props.dayStart as number | undefined;
  const dayEnd = props.dayEnd as number | undefined;
  const titleOverride = props.title as string | undefined;
  const propHour12 = typeof props.hour12 === 'boolean' ? (props.hour12 as boolean) : undefined;
  const propTimezones = useMemo(() => parseTimezones(props.timezones), [props.timezones]);

  const fetched = useVfsContent(eventsUrl);

  const { events, parseError } = useMemo<{
    events: NormalizedEvent[];
    parseError: string | null;
  }>(() => {
    if (eventsUrl) {
      if (!fetched.data) return { events: [], parseError: null };
      const r = parseCalendarData(fetched.data);
      return { events: r.events, parseError: r.error };
    }
    const r = parseCalendarData(inlineEvents);
    return { events: r.events, parseError: r.error };
  }, [eventsUrl, fetched.data, inlineEvents]);

  const data = useMemo(() => getDataDays(events), [events]);

  // Days used to steer the *initial* placement. We prefer timed meetings so a
  // long-running all-day event (e.g. a multi-week "Jamboree" spanning the whole
  // range) doesn't pin the opening view to an otherwise meeting-free week —
  // auto-snap should land the user on their actual meetings. Falls back to all
  // events when the calendar is entirely all-day. Navigation still uses `data`
  // (all events), so the all-day weeks remain reachable.
  const snapData = useMemo(() => {
    const timed = events.filter((e) => !e.allDay);
    return timed.length > 0 ? getDataDays(timed) : data;
  }, [events, data]);

  // Live clock — drives the real-time "now" line. Ticks every 30s.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const [ui, setUi] = useSurfaceNodeUiState<'calendar', CalendarUiState>(
    node.id,
    'calendar',
    { view: initialView, density: initialDensity, anchor: '' },
  );
  const view = ui.view;
  const density = ui.density;

  // Display settings: the user's persisted choice (settings menu) overrides the
  // agent's prop; otherwise fall back to the prop (then locale).
  const effectiveHour12 = ui.hour12 !== undefined ? ui.hour12 : propHour12;
  const effectiveTimezones = ui.timezones !== undefined ? ui.timezones : propTimezones;
  const display = useMemo<CalendarDisplay>(
    () => ({ hour12: effectiveHour12, timezones: effectiveTimezones }),
    [effectiveHour12, effectiveTimezones],
  );
  // The menu radio needs a concrete boolean even when the format is "locale".
  const localeHour12 = useMemo(() => {
    try {
      return new Intl.DateTimeFormat([], { hour: 'numeric' }).resolvedOptions().hour12 ?? true;
    } catch {
      return true;
    }
  }, []);
  const menuHour12 = effectiveHour12 ?? localeHour12;
  const menuTimezones = effectiveTimezones ?? [];

  // Resolve the anchor day: explicit user navigation → prop date → today (if
  // in range) → first data day. Clamp to the data range; snap to a data day
  // for the single-day view (which only ever shows days that have data).
  const anchorDate = useMemo(() => {
    // Explicit navigation (the nav arrows write `ui.anchor`) is honored as-is —
    // auto-snap must never fight the user, e.g. by yanking them back off an
    // all-day-only week they just paged to. Only agent/today placement snaps.
    const userAnchor = ui.anchor ? fromISODate(ui.anchor) : null;
    let base = userAnchor || (propDate && fromISODate(propDate)) || null;
    if (!base) {
      const today = startOfDay(now);
      const inRange =
        data.minDate != null &&
        data.maxDate != null &&
        today.getTime() >= data.minDate.getTime() &&
        today.getTime() <= data.maxDate.getTime();
      base = inRange ? today : data.minDate ?? today;
    }
    if (data.minDate && base.getTime() < data.minDate.getTime()) base = data.minDate;
    if (data.maxDate && base.getTime() > data.maxDate.getTime()) base = data.maxDate;
    if (view === 'day') {
      // The day view only ever shows a day that has data — snap the agent/today
      // anchor onto one (the user's day-nav already lands on data days, so this
      // is a no-op for those). Steer toward meetings via `snapData`.
      base = snapToDataDay(base, userAnchor ? data : snapData);
    } else if (!userAnchor && !viewHasData(view, base, snapData)) {
      // Multi-day views: snap only the *initial* placement, and only when its
      // window holds no meetings (e.g. the agent's `date` landed on a stale work
      // week before the first meeting). Lands on the nearest meeting week.
      base = snapToDataDay(base, snapData);
    }
    return base;
  }, [ui.anchor, propDate, view, data, snapData, now]);

  const viewDays = useMemo(() => getViewDays(view, anchorDate, data), [view, anchorDate, data]);

  const visibleEvents = useMemo(
    () => viewDays.flatMap((d) => eventsForDay(events, d)),
    [viewDays, events],
  );
  const bounds = useMemo(
    () => getTimeBounds(visibleEvents, dayStart, dayEnd),
    [visibleEvents, dayStart, dayEnd],
  );

  // ── Navigation (bounded to data) ──────────────────────────────────────────
  const nav = useMemo(() => {
    if (view === 'day') {
      const prev = prevDataDay(data, anchorDate);
      const next = nextDataDay(data, anchorDate);
      return {
        canPrev: prev !== null,
        canNext: next !== null,
        goPrev: () => prev && setUi({ ...ui, anchor: toISODate(prev) }),
        goNext: () => next && setUi({ ...ui, anchor: toISODate(next) }),
      };
    }
    const prev = stepWeek(view, anchorDate, data, -1);
    const next = stepWeek(view, anchorDate, data, 1);
    return {
      canPrev: prev !== null,
      canNext: next !== null,
      goPrev: () => prev && setUi({ ...ui, anchor: toISODate(prev) }),
      goNext: () => next && setUi({ ...ui, anchor: toISODate(next) }),
    };
  }, [view, anchorDate, data, ui, setUi]);

  // ── States ────────────────────────────────────────────────────────────────
  if (eventsUrl && fetched.loading && events.length === 0) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid="aui-calendar">
        <Skeleton className="mb-3 h-8 w-full" />
        <Skeleton className="mb-2 h-24 w-full" />
        <Skeleton className="h-24 w-2/3" />
      </div>
    );
  }
  if ((eventsUrl && fetched.error) || parseError) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid="aui-calendar">
        <p className="text-sm text-destructive">
          Failed to load calendar: {fetched.error?.message ?? parseError}
        </p>
      </div>
    );
  }

  const eventCount = visibleEvents.length;
  const rangeLabel =
    titleOverride ?? (view === 'day' ? formatDayLong(anchorDate) : formatRangeLabel(viewDays));
  const subLabel =
    events.length === 0
      ? 'No events'
      : `${eventCount} ${eventCount === 1 ? 'event' : 'events'}`;

  // "Today" jump — only offered when today falls within the data range.
  const today = startOfDay(now);
  const todayInRange = !!(
    data.minDate &&
    data.maxDate &&
    today.getTime() >= data.minDate.getTime() &&
    today.getTime() <= data.maxDate.getTime()
  );
  const onToday = todayInRange ? () => setUi({ ...ui, anchor: toISODate(today) }) : undefined;

  return (
    <div
      // pb-2 keeps the view timelines from running flush to the bottom border;
      // mb-6 separates the calendar card from whatever follows it in a surface.
      // Applied here (not per-view) so every view/density and the empty state
      // get the same inner gap + outer spacing.
      className="mb-6 flex flex-col overflow-hidden rounded-lg border border-border bg-card pb-2"
      data-testid="aui-calendar"
    >
      <CalendarHeader
        view={view}
        density={density}
        rangeLabel={rangeLabel}
        subLabel={subLabel}
        onViewChange={(v) => setUi({ ...ui, view: v })}
        onDensityChange={(d) => setUi({ ...ui, density: d })}
        onPrev={nav.goPrev}
        onNext={nav.goNext}
        canPrev={nav.canPrev}
        canNext={nav.canNext}
        onToday={onToday}
        hour12={menuHour12}
        timezones={menuTimezones}
        onHour12Change={(v) => setUi({ ...ui, hour12: v })}
        onTimezonesChange={(tzs) => setUi({ ...ui, timezones: tzs })}
      />

      {events.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          No events to show.
        </div>
      ) : view === 'day' ? (
        <DayView
          events={events}
          day={anchorDate}
          density={density}
          bounds={bounds}
          now={now}
          display={display}
        />
      ) : (
        <WeekView
          events={events}
          days={viewDays}
          density={density}
          bounds={bounds}
          now={now}
          display={display}
        />
      )}
    </div>
  );
}
